import { OrderStatusWire } from '@carcheck/shared';
import type { AdminMetricsGmvPoint, AdminMetricsStatusCount } from '@carcheck/shared';

import { tokens } from '../../../design';

/**
 * Pure SVG-geometry helpers for the dashboard analytics tiles.
 *
 * The backend returns numbers + arrays ONLY (never SVG geometry / chart
 * config — see `admin-metrics.ts` wire doc); the SPA computes the path
 * strings, donut dasharrays and gauge offsets here. Extracted into a pure
 * module so the (fiddly) math is unit-tested in isolation and the tile
 * components stay thin.
 *
 * Everything is zero-safe: an all-zero series yields a flat baseline path
 * (never `NaN` / `Infinity` from a divide-by-zero) so a fresh account with
 * no volume renders a flat chart instead of crashing (guardrail: zero-
 * filled series → flat chart, not a crash).
 */

/** A single polyline/area built from a numeric series over a fixed viewBox. */
export interface SeriesPath {
  /** `d` for the stroked line. */
  line: string;
  /** `d` for the filled area (line + baseline close). */
  area: string;
}

/**
 * Map a numeric series to SVG `x`/`y` points across `width`×`height`, then
 * emit both a line path and a closed area path. `max` is the shared y-scale
 * across ALL series drawn on the same axes (so gmv / paid share scale).
 *
 * - Single point → a flat line at its height (avoids a zero-length path).
 * - `max <= 0` → flat baseline (all values sit on the floor).
 */
export const buildSeriesPath = (
  values: number[],
  width: number,
  height: number,
  max: number,
): SeriesPath => {
  if (values.length === 0) {
    const mid = height;
    return { line: `M 0 ${mid} L ${width} ${mid}`, area: '' };
  }
  const safeMax = max > 0 ? max : 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = values.length > 1 ? i * stepX : width / 2;
    const y = height - (Math.max(v, 0) / safeMax) * height;
    return { x, y };
  });

  const line = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${round(p.x)} ${round(p.y)}`)
    .join(' ');

  const first = points[0];
  const last = points[points.length - 1];
  const area =
    first && last ? `${line} L ${round(last.x)} ${height} L ${round(first.x)} ${height} Z` : '';

  return { line, area };
};

/** Bars for the refunds series (drawn under the gmv area in the GMV tile). */
export interface BarRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Options for {@link buildBars} — grouped to keep the arity ≤ 4. */
export interface BuildBarsOptions {
  values: number[];
  width: number;
  height: number;
  max: number;
  /** Fixed bar width; defaults to 60% of a slot (min 4px). */
  barWidth?: number;
}

/**
 * Lay out one bar per series entry across the viewBox, heights scaled to
 * `max`. Zero / negative values collapse to zero-height (skipped by the
 * renderer). `max <= 0` → all bars zero-height.
 */
export const buildBars = ({ values, width, height, max, barWidth }: BuildBarsOptions): BarRect[] => {
  if (values.length === 0) return [];
  const safeMax = max > 0 ? max : 1;
  const slot = width / values.length;
  const bw = barWidth ?? Math.max(4, slot * 0.6);
  return values.map((v, i) => {
    const h = (Math.max(v, 0) / safeMax) * height;
    return {
      x: round(i * slot + slot / 2 - bw / 2),
      y: round(height - h),
      width: bw,
      height: round(h),
    };
  });
};

/** The shared y-scale across the GMV tile's three series. */
export const seriesMax = (series: AdminMetricsGmvPoint[]): number =>
  series.reduce((m, p) => Math.max(m, p.gmv, p.paid, p.refunds), 0);

/** Donut circumference for r = 50 (the mockup radius). */
export const DONUT_RADIUS = 50;
export const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** One drawable donut arc: dash length + negative offset + colour + meta. */
export interface DonutSegment {
  status: string;
  count: number;
  /** Fraction of the whole (0..1). */
  fraction: number;
  /** `stroke-dasharray` visible length (px along the circumference). */
  dash: number;
  /** `stroke-dashoffset` (negative cumulative length). */
  offset: number;
  color: string;
}

/** Wire `OrderStatus` → donut slice colour. Unknown → tertiary grey. */
export const orderStatusColor = (status: string): string => {
  switch (status) {
    case OrderStatusWire.Completed:
      return tokens.brand.primary;
    case OrderStatusWire.InProgress:
      return tokens.state.info;
    case OrderStatusWire.Paid:
    case OrderStatusWire.Accepted:
    case OrderStatusWire.Proposed:
      return tokens.state.warning;
    case OrderStatusWire.Cancelled:
    case OrderStatusWire.Refunded:
      return tokens.state.danger;
    default:
      return tokens.text.tertiary;
  }
};

/**
 * Build the donut arcs from the per-status counts. Segments accumulate a
 * running offset so they chain around the ring. `total <= 0` → no segments
 * (the tile renders the empty track only → flat/empty donut, not a crash).
 */
export const buildDonutSegments = (
  byStatus: AdminMetricsStatusCount[],
  total: number,
): DonutSegment[] => {
  if (total <= 0) return [];
  let cursor = 0;
  return byStatus
    .filter((s) => s.count > 0)
    .map((s) => {
      const fraction = s.count / total;
      const dash = fraction * DONUT_CIRCUMFERENCE;
      const segment: DonutSegment = {
        status: s.status,
        count: s.count,
        fraction,
        dash,
        offset: -cursor,
        color: orderStatusColor(s.status),
      };
      cursor += dash;
      return segment;
    });
};

/**
 * Gauge stroke-dashoffset for a ring of the given circumference showing
 * `value/total`. Clamped to [0, 1]; `total <= 0` → empty (full offset).
 */
export const gaugeOffset = (value: number, total: number, circumference: number): number => {
  if (total <= 0) return circumference;
  const pct = Math.min(Math.max(value / total, 0), 1);
  return circumference * (1 - pct);
};

/** Integer percentage `value/total` (0 when total is 0). */
export const percent = (value: number, total: number): number => {
  if (total <= 0) return 0;
  return Math.round((value / total) * 100);
};

const round = (n: number): number => Math.round(n * 100) / 100;
